import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { KeyRound, Link, Pencil, Play, Save, Settings } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import ParticleService from '../ParticleService';

const initialSettings = {
  usdMonthly: '', usdAnnual: '', cadMonthly: '', cadAnnual: '',
  helcimCadMonthlyPlanId: '', helcimCadAnnualPlanId: '', helcimUsdMonthlyPlanId: '', helcimUsdAnnualPlanId: '',
  helcimCadTaxEnabled: true, helcimUsdTaxEnabled: false,
  freePeriodDays: 365, activationWindowDays: 90, gracePeriodDays: 14,
  simDeactivationDelayDays: 14, particleUnclaimDelayDays: 180,
  enforcementMode: 'Observe', auditRetentionYears: 7, reason: '',
};

const emptyProductMapping = {
  id: null, store: 'Canada', productId: '', variationId: '', sku: '', name: '', includedDeviceCount: 1, isActive: true, reason: '',
};

const NumberField = ({ label, name, value, onChange, min, max, step = 1, help, required = true }) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <Form.Control type="number" required={required} min={min} max={max} step={step} name={name} value={value} onChange={onChange} />
    {help && <Form.Text className="text-muted">{help}</Form.Text>}
  </Form.Group>
);

export default function SubscriptionSettings() {
  const [form, setForm] = useState(initialSettings);
  const [integrations, setIntegrations] = useState(null);
  const [productMappings, setProductMappings] = useState([]);
  const [mappingForm, setMappingForm] = useState(emptyProductMapping);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [alertTesting, setAlertTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenanceRunning, setMaintenanceRunning] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [showMaintenanceConfirm, setShowMaintenanceConfirm] = useState(false);
  const [recovery, setRecovery] = useState({ checkoutSessionId: '', externalSubscriptionId: '' });
  const [recoveryRunning, setRecoveryRunning] = useState(false);

  useEffect(() => {
    Promise.all([
      ParticleService.getSubscriptionSettings(),
      ParticleService.getIntegrationReadiness(),
      ParticleService.getWooCommerceProductMappings(),
    ]).then(([result, integrationResult, mappingResult]) => {
      if (result.error) toast.error(result.detail || result.error);
      else setForm({
        ...initialSettings,
        ...result,
        cadMonthly: result.cadMonthly ?? '',
        cadAnnual: result.cadAnnual ?? '',
        helcimCadMonthlyPlanId: result.helcimCadMonthlyPlanId ?? '',
        helcimCadAnnualPlanId: result.helcimCadAnnualPlanId ?? '',
        helcimUsdMonthlyPlanId: result.helcimUsdMonthlyPlanId ?? '',
        helcimUsdAnnualPlanId: result.helcimUsdAnnualPlanId ?? '',
        reason: '',
      });
      if (integrationResult.error) toast.error(integrationResult.detail || integrationResult.error);
      else setIntegrations(integrationResult);
      if (mappingResult.error) toast.error(mappingResult.detail || mappingResult.error);
      else setProductMappings(mappingResult);
      setLoading(false);
    });
  }, []);

  const change = event => setForm(current => ({
    ...current,
    [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
  }));
  const mappingChange = event => setMappingForm(current => ({
    ...current,
    [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
  }));
  const sendAlertTest = async () => {
    setAlertTesting(true);
    const result = await ParticleService.sendOperationalAlertTest();
    if (result.error) toast.error(result.detail || result.error);
    else toast.success(`Operational alert test sent to ${result.recipientCount} recipient(s).`);
    setAlertTesting(false);
  };

  const saveMapping = async event => {
    event.preventDefault();
    setMappingSaving(true);
    const body = {
      store: mappingForm.store,
      productId: Number(mappingForm.productId),
      variationId: mappingForm.variationId === '' ? null : Number(mappingForm.variationId),
      sku: mappingForm.sku.trim() || null,
      name: mappingForm.name.trim(),
      includedDeviceCount: Number(mappingForm.includedDeviceCount),
      isActive: mappingForm.isActive,
      reason: mappingForm.reason.trim(),
    };
    const result = mappingForm.id
      ? await ParticleService.updateWooCommerceProductMapping(mappingForm.id, body)
      : await ParticleService.createWooCommerceProductMapping(body);
    if (result.error) toast.error(result.detail || result.error);
    else {
      setProductMappings(current => [...current.filter(mapping => mapping.id !== result.id), result]
        .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.store.localeCompare(b.store) || a.name.localeCompare(b.name)));
      setMappingForm(emptyProductMapping);
      toast.success('WooCommerce product mapping saved and audited.');
    }
    setMappingSaving(false);
  };

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    const body = {
      usdMonthly: Number(form.usdMonthly), usdAnnual: Number(form.usdAnnual),
      cadMonthly: Number(form.cadMonthly), cadAnnual: Number(form.cadAnnual),
      helcimCadMonthlyPlanId: form.helcimCadMonthlyPlanId === '' ? null : Number(form.helcimCadMonthlyPlanId),
      helcimCadAnnualPlanId: form.helcimCadAnnualPlanId === '' ? null : Number(form.helcimCadAnnualPlanId),
      helcimUsdMonthlyPlanId: form.helcimUsdMonthlyPlanId === '' ? null : Number(form.helcimUsdMonthlyPlanId),
      helcimUsdAnnualPlanId: form.helcimUsdAnnualPlanId === '' ? null : Number(form.helcimUsdAnnualPlanId),
      helcimCadTaxEnabled: form.helcimCadTaxEnabled,
      helcimUsdTaxEnabled: form.helcimUsdTaxEnabled,
      freePeriodDays: Number(form.freePeriodDays), activationWindowDays: Number(form.activationWindowDays),
      gracePeriodDays: Number(form.gracePeriodDays), simDeactivationDelayDays: Number(form.simDeactivationDelayDays),
      particleUnclaimDelayDays: Number(form.particleUnclaimDelayDays), enforcementMode: form.enforcementMode,
      auditRetentionYears: Number(form.auditRetentionYears), reason: form.reason.trim(),
    };
    const result = await ParticleService.updateSubscriptionSettings(body);
    if (result.error) toast.error(result.detail || result.error);
    else {
      setForm(current => ({
        ...current, ...result,
        helcimCadMonthlyPlanId: result.helcimCadMonthlyPlanId ?? '',
        helcimCadAnnualPlanId: result.helcimCadAnnualPlanId ?? '',
        helcimUsdMonthlyPlanId: result.helcimUsdMonthlyPlanId ?? '',
        helcimUsdAnnualPlanId: result.helcimUsdAnnualPlanId ?? '',
        reason: '',
      }));
      toast.success('Subscription settings saved and audited.');
    }
    setSaving(false);
  };

  const runMaintenance = async () => {
    setShowMaintenanceConfirm(false);
    setMaintenanceRunning(true);
    const result = await ParticleService.runSubscriptionMaintenance();
    if (result.error) toast.error(result.detail || result.error);
    else {
      setMaintenanceResult(result);
      toast.success('Subscription maintenance completed in observation mode.');
    }
    setMaintenanceRunning(false);
  };

  const adoptSubscription = async event => {
    event.preventDefault();
    setRecoveryRunning(true);
    const result = await ParticleService.adoptHelcimSubscription(
      recovery.checkoutSessionId.trim(), recovery.externalSubscriptionId);
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(`Helcim subscription ${result.externalSubscriptionId} was linked successfully.`);
      setRecovery({ checkoutSessionId: '', externalSubscriptionId: '' });
    }
    setRecoveryRunning(false);
  };

  if (loading) return <div className="text-center p-5"><Spinner animation="border" /><div className="mt-2">Loading settings…</div><ToastContainer /></div>;

  return (
    <div className="container-fluid p-4">
      <div className="mb-4">
        <h2 className="mb-1 d-flex align-items-center gap-2"><Settings size={25} /> Subscription Settings</h2>
        <p className="text-muted mb-0">Global pricing and entitlement policy. Changes affect future policy calculations and are audited.</p>
      </div>

      <Form onSubmit={save}>
        <Row>
          <Col xl={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white"><strong>Subscription pricing</strong></Card.Header>
              <Card.Body>
                <Alert variant="info">Prices are dashboard-subscription prices only. Physical devices remain one-time purchases. Set a price to <strong>0</strong> to hide that option from customers.</Alert>
                <Row>
                  <Col md={6}><NumberField label="USD monthly" name="usdMonthly" value={form.usdMonthly} onChange={change} min="0" max="100000" step="0.01" /></Col>
                  <Col md={6}><NumberField label="USD annual" name="usdAnnual" value={form.usdAnnual} onChange={change} min="0" max="100000" step="0.01" /></Col>
                  <Col md={6}><NumberField label="CAD monthly" name="cadMonthly" value={form.cadMonthly} onChange={change} min="0" max="100000" step="0.01" /></Col>
                  <Col md={6}><NumberField label="CAD annual" name="cadAnnual" value={form.cadAnnual} onChange={change} min="0" max="100000" step="0.01" /></Col>
                </Row>
                <hr />
                <p className="small text-muted">Recurring plan IDs are non-secret identifiers from the matching Helcim merchant account. Leave them blank until those plans exist.</p>
                <Row>
                  <Col md={6}><NumberField label="Helcim CAD monthly plan ID" name="helcimCadMonthlyPlanId" value={form.helcimCadMonthlyPlanId} onChange={change} min="1" required={false} /></Col>
                  <Col md={6}><NumberField label="Helcim CAD annual plan ID" name="helcimCadAnnualPlanId" value={form.helcimCadAnnualPlanId} onChange={change} min="1" required={false} /></Col>
                  <Col md={6}><NumberField label="Helcim USD monthly plan ID" name="helcimUsdMonthlyPlanId" value={form.helcimUsdMonthlyPlanId} onChange={change} min="1" required={false} /></Col>
                  <Col md={6}><NumberField label="Helcim USD annual plan ID" name="helcimUsdAnnualPlanId" value={form.helcimUsdAnnualPlanId} onChange={change} min="1" required={false} /></Col>
                </Row>
                <Alert variant="secondary" className="small">
                  These switches must match the tax settings chosen in Helcim. They validate checkout configuration only and never modify a Helcim plan.
                </Alert>
                <Row>
                  <Col md={6}>
                    <Form.Check type="switch" className="mb-3" id="helcim-cad-tax-enabled" name="helcimCadTaxEnabled" checked={form.helcimCadTaxEnabled} onChange={change} label="CAD Helcim plans charge tax" />
                  </Col>
                  <Col md={6}>
                    <Form.Check type="switch" className="mb-3" id="helcim-usd-tax-enabled" name="helcimUsdTaxEnabled" checked={form.helcimUsdTaxEnabled} onChange={change} label="USD Helcim plans charge tax" />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white"><strong>Included access and renewal</strong></Card.Header>
              <Card.Body>
                <NumberField label="Included free period (days)" name="freePeriodDays" value={form.freePeriodDays} onChange={change} min="1" max="3650" help="Default: 365 days after activation." />
                <NumberField label="Maximum activation window (days)" name="activationWindowDays" value={form.activationWindowDays} onChange={change} min="0" max="365" help="Free access starts automatically when this window expires." />
                <NumberField label="Payment grace period (days)" name="gracePeriodDays" value={form.gracePeriodDays} onChange={change} min="0" max="90" help="Dashboard access remains available during this period." />
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white"><strong>Expiry enforcement policy</strong></Card.Header>
              <Card.Body>
                <Alert variant="warning">Automated enforcement is not active yet. Keep <strong>Observe</strong> selected during local Phase 5 testing.</Alert>
                {maintenanceResult && <Alert variant="success" className="small">Last manual run: {maintenanceResult.freePeriodsActivated} free period(s) activated, {maintenanceResult.statusesChanged} status(es) updated, and {maintenanceResult.commandsQueued} enforcement command(s) queued.</Alert>}
                <Form.Group className="mb-3">
                  <Form.Label>Enforcement mode</Form.Label>
                  <Form.Select name="enforcementMode" value={form.enforcementMode} onChange={change}>
                    <option value="Observe">Observe only</option>
                    <option value="RequireApproval">Require administrator approval</option>
                    <option value="Automatic">Automatic</option>
                  </Form.Select>
                </Form.Group>
                <NumberField label="SIM deactivation delay (days)" name="simDeactivationDelayDays" value={form.simDeactivationDelayDays} onChange={change} min="0" max="365" help="Measured after dashboard suspension." />
                <NumberField label="Particle unclaim delay (days)" name="particleUnclaimDelayDays" value={form.particleUnclaimDelayDays} onChange={change} min="0" max="3650" help="Cannot be earlier than SIM deactivation." />
                <Button type="button" variant="outline-secondary" disabled={maintenanceRunning} onClick={() => setShowMaintenanceConfirm(true)}><Play size={16} className="me-2" />{maintenanceRunning ? 'Running maintenance…' : 'Run maintenance now'}</Button>
                <Form.Text className="d-block text-muted mt-2">This may activate due free periods and update local statuses, but it never queues enforcement commands.</Form.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white"><strong>Governance and secrets</strong></Card.Header>
              <Card.Body>
                <NumberField label="Audit retention (years)" name="auditRetentionYears" value={form.auditRetentionYears} onChange={change} min="7" max="25" />
                <Alert variant="secondary" className="d-flex gap-2 align-items-start">
                  <KeyRound size={20} className="mt-1 flex-shrink-0" />
                  <div><strong>Credentials and tokens are not stored here.</strong><br />Production secrets will use Azure Key Vault references so raw values are never returned to the browser or saved as ordinary database settings.</div>
                </Alert>
                {integrations && <div className="border rounded p-3 mb-3">
                  <strong className="d-block mb-2">Payment integration readiness</strong>
                  <IntegrationRow label="WooCommerce Canada webhook" ready={integrations.wooCommerceCanada.webhookConfigured} />
                  <IntegrationRow label="WooCommerce Canada order API" ready={integrations.wooCommerceCanada.apiConfigured} />
                  <IntegrationRow label="WooCommerce U.S. webhook" ready={integrations.wooCommerceUnitedStates.webhookConfigured} />
                  <IntegrationRow label="WooCommerce U.S. order API" ready={integrations.wooCommerceUnitedStates.apiConfigured} />
                  <IntegrationRow label="WooCommerce order processing" ready={integrations.wooCommerceCanada.processingEnabled} inactiveLabel="Disabled" />
                  <IntegrationRow label="Helcim CAD API" ready={integrations.helcimCad.apiConfigured} />
                  <IntegrationRow label="Helcim CAD webhook" ready={integrations.helcimCad.webhookConfigured} />
                  <IntegrationRow label="Helcim CAD monthly plan" ready={integrations.helcimCad.monthlyPlanConfigured} />
                  <IntegrationRow label="Helcim CAD annual plan" ready={integrations.helcimCad.annualPlanConfigured} />
                  <IntegrationRow label="Helcim USD API" ready={integrations.helcimUsd.apiConfigured} />
                  <IntegrationRow label="Helcim USD webhook" ready={integrations.helcimUsd.webhookConfigured} />
                  <IntegrationRow label="Helcim USD monthly plan" ready={integrations.helcimUsd.monthlyPlanConfigured} />
                  <IntegrationRow label="Helcim USD annual plan" ready={integrations.helcimUsd.annualPlanConfigured} />
                  <IntegrationRow label="Operational failure email alerts" ready={integrations.operationalAlerts?.ready} inactiveLabel={integrations.operationalAlerts?.enabled ? 'Email or recipient missing' : 'Disabled'} />
                  <Button type="button" className="mt-2" size="sm" variant="outline-primary" disabled={!integrations.operationalAlerts?.canSendTest || alertTesting} onClick={sendAlertTest}>{alertTesting ? 'Sending test…' : 'Send test alert email'}</Button>
                  <div className="small text-muted mt-2">Only configuration status is shown; secret values are never sent to the browser.</div>
                </div>}
                <Form.Group>
                  <Form.Label>Reason for change</Form.Label>
                  <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} name="reason" value={form.reason} onChange={change} placeholder="Required for the audit history" />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button type="submit" size="lg" disabled={saving}><Save size={18} className="me-2" />{saving ? 'Saving…' : 'Save settings'}</Button>
        </div>
      </Form>
      <Card className="shadow-sm mt-4">
        <Card.Header className="bg-white"><strong>WooCommerce device products</strong></Card.Header>
        <Card.Body>
          <Alert variant="info">Mappings are store-specific because the separate Canada and U.S. WordPress installations may reuse product IDs and SKUs. A matching paid order line creates an unfulfilled included-access allocation; it does not start the free year until a device is fulfilled and assigned.</Alert>
          {productMappings.length === 0 ? <p className="text-muted">No device products are mapped yet.</p> :
            <div className="table-responsive mb-4"><table className="table align-middle">
              <thead><tr><th>Store</th><th>Name</th><th>Product ID</th><th>Variation</th><th>SKU</th><th>Devices/item</th><th>Status</th><th></th></tr></thead>
              <tbody>{productMappings.map(mapping => <tr key={mapping.id}>
                <td>{mapping.store === 'UnitedStates' ? 'U.S.' : 'Canada'}</td><td>{mapping.name}</td><td>{mapping.productId}</td><td>{mapping.variationId || 'Any'}</td><td>{mapping.sku || '—'}</td>
                <td>{mapping.includedDeviceCount}</td><td>{mapping.isActive ? <span className="text-success fw-bold">Active</span> : <span className="text-muted">Inactive</span>}</td>
                <td><Button size="sm" variant="outline-primary" onClick={() => setMappingForm({ ...mapping, variationId: mapping.variationId ?? '', sku: mapping.sku ?? '', reason: '' })}><Pencil size={14} className="me-1" />Edit</Button></td>
              </tr>)}</tbody>
            </table></div>}
          <Form onSubmit={saveMapping}>
            <Row>
              <Col md={2}><Form.Group className="mb-3"><Form.Label>Store</Form.Label><Form.Select name="store" value={mappingForm.store} onChange={mappingChange}><option value="Canada">Canada</option><option value="UnitedStates">United States</option></Form.Select></Form.Group></Col>
              <Col md={2}><Form.Group className="mb-3"><Form.Label>Display name</Form.Label><Form.Control required minLength={2} maxLength={200} name="name" value={mappingForm.name} onChange={mappingChange} /></Form.Group></Col>
              <Col md={2}><NumberField label="Product ID" name="productId" value={mappingForm.productId} onChange={mappingChange} min="1" /></Col>
              <Col md={2}><NumberField label="Variation ID (optional)" name="variationId" value={mappingForm.variationId} onChange={mappingChange} min="1" required={false} /></Col>
              <Col md={2}><Form.Group className="mb-3"><Form.Label>SKU (optional)</Form.Label><Form.Control maxLength={100} name="sku" value={mappingForm.sku} onChange={mappingChange} /></Form.Group></Col>
              <Col md={2}><NumberField label="Devices per item" name="includedDeviceCount" value={mappingForm.includedDeviceCount} onChange={mappingChange} min="1" max="100" /></Col>
            </Row>
            <Row className="align-items-end">
              <Col md={8}><Form.Group className="mb-3"><Form.Label>Reason</Form.Label><Form.Control required minLength={5} maxLength={500} name="reason" value={mappingForm.reason} onChange={mappingChange} placeholder="Required for the audit history" /></Form.Group></Col>
              <Col md={2}><Form.Check className="mb-3" type="switch" label="Active" name="isActive" checked={mappingForm.isActive} onChange={mappingChange} /></Col>
              <Col md={2} className="mb-3 d-grid"><Button type="submit" disabled={mappingSaving}>{mappingSaving ? 'Saving…' : mappingForm.id ? 'Update mapping' : 'Add mapping'}</Button></Col>
            </Row>
            {mappingForm.id && <Button type="button" variant="link" className="p-0" onClick={() => setMappingForm(emptyProductMapping)}>Cancel editing</Button>}
          </Form>
        </Card.Body>
      </Card>
      <Card className="shadow-sm mt-4">
        <Card.Header className="bg-white"><strong className="d-flex align-items-center gap-2"><Link size={18} /> Recover an existing Helcim subscription</strong></Card.Header>
        <Card.Body>
          <Alert variant="warning">Use this only when Helcim created a subscription but Cannon Control did not record it. The subscription is validated against the verified checkout before anything is saved. This action never creates or changes a Helcim subscription.</Alert>
          <Form onSubmit={adoptSubscription}>
            <Row>
              <Col md={5}><Form.Group className="mb-3"><Form.Label>Verified checkout session ID</Form.Label><Form.Control required pattern="[0-9a-fA-F-]{36}" value={recovery.checkoutSessionId} onChange={event => setRecovery(current => ({ ...current, checkoutSessionId: event.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" /></Form.Group></Col>
              <Col md={4}><NumberField label="Helcim subscription ID" name="externalSubscriptionId" value={recovery.externalSubscriptionId} onChange={event => setRecovery(current => ({ ...current, externalSubscriptionId: event.target.value }))} min="1" /></Col>
              <Col md={3} className="d-flex align-items-end mb-3"><Button type="submit" className="w-100" disabled={recoveryRunning}>{recoveryRunning ? 'Validating…' : 'Validate and link'}</Button></Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      <Modal show={showMaintenanceConfirm} onHide={() => setShowMaintenanceConfirm(false)} centered>
        <Modal.Header closeButton><Modal.Title>Run subscription maintenance?</Modal.Title></Modal.Header>
        <Modal.Body>
          This will create any included free periods whose activation deadlines have arrived and refresh locally managed subscription statuses. It will not block dashboard access, deactivate SIMs, or unclaim devices.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMaintenanceConfirm(false)}>Cancel</Button>
          <Button variant="primary" onClick={runMaintenance}>Run maintenance</Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer />
    </div>
  );
}

const IntegrationRow = ({ label, ready, inactiveLabel = 'Not configured' }) => (
  <div className="d-flex justify-content-between gap-3 small py-1">
    <span>{label}</span>
    <span className={ready ? 'text-success fw-bold' : 'text-muted'}>{ready ? 'Configured' : inactiveLabel}</span>
  </div>
);
